import { useState } from 'react';
import { Box, IconButton, Link, Popover, Tooltip, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { snowUrl } from 'service/config';
import { ReactComponent as KbArticleBlueIcon } from 'assets/PepWise2.0/kb-article-blue.svg';
import { ReactComponent as KbArticleGreyIcon } from 'assets/PepWise2.0/kb-article-grey.svg';

export interface KbArticle {
  sysId: string;
  number?: string;
  title?: string;
}

interface KbArticlesCellProps {
  articles: KbArticle[];
}

const iconButtonSx = {
  p: 0.5,
  '& svg': {
    width: 24,
    height: 24,
  },
};

const KbArticlesCell = ({ articles }: KbArticlesCellProps) => {
  const theme = useTheme();
  const colors = theme.palette;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!articles.length) {
    return (
      <Tooltip title='No KB Articles' arrow>
        <Box component='span' sx={{ display: 'inline-flex', ...iconButtonSx }}>
          <KbArticleGreyIcon />
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title='KB Articles Available' arrow>
        <IconButton
          size='small'
          aria-label='View KB Articles'
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={iconButtonSx}
        >
          <KbArticleBlueIcon />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 200,
            maxWidth: 320,
            borderRadius: 1,
            boxShadow: theme.shadows[4],
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pl: 1.5,
            pr: 0.5,
            py: 0.5,
            backgroundColor: colors.table.headerBg,
          }}
        >
          <Typography
            variant='tableRowFont'
            sx={{
              fontWeight: 600,
              textTransform: 'uppercase',
              color: `${colors.table.headerText} !important`,
            }}
          >
            KB Articles
          </Typography>
          <IconButton size='small' aria-label='Close' onClick={() => setAnchorEl(null)} sx={{ p: 0.5 }}>
            <CloseIcon fontSize='small' sx={{ color: colors.table.headerText }} />
          </IconButton>
        </Box>
        {articles.map((article, index) => (
          <Box
            key={article.sysId}
            sx={{
              px: 1.5,
              py: 1,
              backgroundColor: index % 2 === 0 ? colors.table.oddRowBgColor : colors.table.evenRowBgColor,
            }}
          >
            <Link
              variant='tableRowFont'
              href={`${snowUrl}/now/sow/kb_view/kb_knowledge/${article.sysId}`}
              target='_blank'
              rel='noopener noreferrer'
              title={article.title}
              sx={{
                color: `${colors.table.moreIcon} !important`,
                textDecorationColor: 'inherit',
              }}
            >
              {article.number ?? `KB Article ${index + 1}`}
            </Link>
          </Box>
        ))}
      </Popover>
    </>
  );
};

export default KbArticlesCell;
